/**
 * Date/time helpers for HTML datetime-local inputs.
 *
 * Critical context: this platform sells events in Argentina (UTC-3, no DST).
 * Producers always enter event times in Argentina-local time, regardless of the
 * physical timezone of the device they are using.
 *
 * The old implementation relied on `new Date(...)` which interprets naive
 * strings using the BROWSER's timezone — meaning a producer using a laptop set
 * to UTC (or any non-ART timezone, common on Macbooks travelling, VPNs, or
 * cloud setups) would see their "23:00" typed in get stored as a different
 * absolute moment and re-displayed as a different hour (often "00:00").
 *
 * Fix: pin every conversion to `America/Argentina/Buenos_Aires` via
 * date-fns-tz, so the round-trip is always:
 *   user types "23:00" → stored UTC = 02:00 next day → re-displayed "23:00".
 */

import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

const AR_TZ = 'America/Argentina/Buenos_Aires';

/**
 * Convert an ISO 8601 string into the YYYY-MM-DDTHH:mm representation in
 * Argentina time — ready to feed into a <input type="datetime-local">.
 */
export const isoToLocalDateTimeInput = (iso) => {
  if (!iso) return '';
  try {
    return formatInTimeZone(new Date(iso), AR_TZ, "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
};

/**
 * Convert a YYYY-MM-DDTHH:mm value from <input type="datetime-local"> —
 * interpreted as Argentina-local time — into a UTC ISO 8601 string.
 */
export const localDateTimeInputToISO = (value) => {
  if (!value) return null;
  try {
    return fromZonedTime(value, AR_TZ).toISOString();
  } catch {
    return null;
  }
};

/**
 * Combine separate date (YYYY-MM-DD) and time (HH:mm) inputs into a UTC ISO
 * string, interpreting them as Argentina-local time.
 */
export const localDateAndTimeToISO = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  return localDateTimeInputToISO(`${dateStr}T${timeStr}`);
};

/**
 * Split a UTC ISO into Argentina-local date (YYYY-MM-DD) and time (HH:mm).
 */
export const splitIsoToLocalParts = (iso) => {
  const v = isoToLocalDateTimeInput(iso);
  if (!v) return { date: '', time: '' };
  const [date, time] = v.split('T');
  return { date, time };
};

/**
 * Format a UTC ISO date for human-friendly Argentina display, e.g.
 *   "viernes, 7 de marzo, 23:00 hs"
 */
export const formatArgentinaLong = (iso) => {
  if (!iso) return '';
  try {
    return formatInTimeZone(new Date(iso), AR_TZ, "EEEE, d 'de' MMMM, HH:mm 'hs'");
  } catch {
    return '';
  }
};
